/**
 * Client-side Role-Based Access Control.
 *
 * Defines the 4 ARIA-spec role tiers + a super-admin role. Each role gets an
 * allow-list of frontend route paths. Backend remains the source of truth —
 * the sidebar uses this map only to hide entries the user is not entitled to.
 *
 * Roles (per ARIA persona spec):
 *   physician        — full clinical, full document signing, full billing
 *   np_pa            — full clinical access; outputs auto-flagged for physician co-sign
 *   ma_scribe        — data entry only; no document approval; no billing
 *   front_desk       — billing, scheduling, referral tracking; no clinical modules
 *   admin            — super admin (security command center + all modules)
 *
 * Legacy roles (kept for backward compat with previously seeded accounts):
 *   biller           — treat as front_desk
 *   care_coordinator — treat as np_pa
 */

const ALL_MODULES = [
  "/dashboard", "/charting", "/prior-auth", "/liability", "/referrals",
  "/predictive", "/risk-stratification", "/emergency-protocols",
  "/revenue-cycle", "/legal-intelligence", "/contract-intelligence",
  "/pa-np-hub", "/peer-consult", "/voice-engine", "/dictation",
  "/encounter-prep", "/time-saved",
  "/staffing", "/ehr-bridge", "/managed-care", "/hospital-ops",
  "/care-coordination", "/pharma-manager", "/cme-tracker", "/telehealth",
  "/patient-engagement", "/reputation-manager",
  "/outcomes", "/cardiology",
  "/financial-wellness", "/data-intelligence", "/analytics",
];

const ACCESS = {
  admin: new Set([...ALL_MODULES, "/security-admin"]),
  physician: new Set(ALL_MODULES),
  np_pa: new Set([
    "/dashboard", "/charting", "/prior-auth", "/liability", "/referrals",
    "/predictive", "/risk-stratification", "/emergency-protocols",
    "/pa-np-hub", "/peer-consult", "/voice-engine", "/dictation",
    "/encounter-prep", "/time-saved",
    "/care-coordination", "/pharma-manager", "/cme-tracker",
    "/patient-engagement", "/ehr-bridge", "/outcomes", "/cardiology",
  ]),
  ma_scribe: new Set([
    "/dashboard", "/charting", "/voice-engine", "/dictation",
    "/encounter-prep", "/time-saved",
    "/patient-engagement", "/ehr-bridge", "/care-coordination",
  ]),
  front_desk: new Set([
    "/dashboard", "/revenue-cycle", "/managed-care", "/staffing",
    "/patient-engagement", "/reputation-manager", "/referrals",
    "/contract-intelligence", "/telehealth", "/outcomes",
  ]),
  // legacy aliases
  biller: new Set([
    "/dashboard", "/revenue-cycle", "/managed-care",
    "/contract-intelligence", "/analytics", "/patient-engagement",
  ]),
  care_coordinator: new Set([
    "/dashboard", "/care-coordination", "/patient-engagement",
    "/referrals", "/risk-stratification", "/pa-np-hub",
    "/ehr-bridge", "/cme-tracker",
  ]),
};

export const ROLE_LABELS = {
  physician: "Physician",
  np_pa: "NP / PA",
  ma_scribe: "Medical Assistant / Scribe",
  front_desk: "Front Desk / Admin",
  admin: "Super Admin",
  biller: "Biller (legacy)",
  care_coordinator: "Care Coordinator (legacy)",
};

export const ROLE_TIERS = [
  { value: "physician", label: "Physician (full clinical access)" },
  { value: "np_pa", label: "NP / PA (clinical, requires physician co-sign)" },
  { value: "ma_scribe", label: "Medical Assistant / Scribe (data entry only)" },
  { value: "front_desk", label: "Front Desk / Admin (billing & scheduling)" },
  { value: "admin", label: "Super Admin (security + all modules)" },
];

export function canAccess(role, path) {
  if (!role) return false;
  const set = ACCESS[role];
  if (!set) return false;
  return set.has(path);
}

export function requiresCoSign(role) {
  return role === "np_pa" || role === "care_coordinator";
}

export function isDataEntryOnly(role) {
  return role === "ma_scribe";
}
